import { NgModule } from '@angular/core';
import {
  NoPreloading,
  PreloadAllModules,
  PreloadingStrategy,
  RouterModule,
  Routes,
} from '@angular/router';
import { ValidarModoGuard } from './pages/login/guards/validar-modo.guard';
import { ValidarTokenGuard } from './pages/login/guards/validar-token.guard';
import { HomePageComponent } from './pages/home/home.page.component';
import { environment } from '../environments/environment';
import { noProducionGuard } from './shared/guards/noProducion.guard';
import { rolesGuard } from './pages/login/guards/roles.guard';
import { userGroupsGuard } from './pages/login/guards/user-groups.guard';
import { HIDRAULICA_GROUPS } from './pages/login/guards/module-access-policy';

const routes: Routes = [
  {
    path: '',
    component: HomePageComponent,
    canActivate: [ValidarTokenGuard, ValidarModoGuard],
    canLoad: [ValidarTokenGuard, ValidarModoGuard],
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import(
            './pages/home/components/welcome-component/welcome-component.component'
          ).then(c => c.WelcomeComponent),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
      },

      {
        path: 'configuracion',
        loadComponent: () =>
          import('./pages/configuracion/configuracion.page.component').then(
            c => c.ConfiguracionPageComponent
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
      },

      {
        path: 'obtener-geojson',
        loadComponent: () =>
          import('./pages/obtener-geojson/obtener-geojson.page.component').then(
            c => c.ObtenerGeojsonPageComponent
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
      },
      // Modulo Hidraulica
      {
        path: 'hidraulica',
        loadChildren: () =>
          import('./pages/hidraulico/hidraulico.module').then(
            m => m.HidraulicoPageModule
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [userGroupsGuard(HIDRAULICA_GROUPS)],
      },

      {
        path: 'gestion',
        loadChildren: () =>
          import('./pages/gestion/gestion.routes').then(m => m.gestionRoutes),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['gestion'])],
      },

      {
        path: 'cuaderno-de-campo',
        loadComponent: () =>
          import('./pages/cuaderno-de-campo/cuaderno-de-campo').then(
            c => c.CuadernoDeCampo
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
      },

      {
        path: 'api-publica',
        loadComponent: () =>
          import('./pages/api-publica/api-publica.page.component').then(
            c => c.ApiPublicaPageComponent
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['api-publica'])],
      },

      {
        path: 'energia',
        loadChildren: () =>
          import('./pages/energia/energia.module').then(m => m.EnergiaModule),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['energia'])],
      },

      {
        path: 'alarmas',
        loadChildren: () =>
          import('./pages/alarmas/alarmas.module').then(m => m.AlarmasModule),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['alarmas'])],
      },

      // *** Sin el token correcto a las siguentes rutas no podrá accederse ***
      {
        path: 'cexplotacion/:data',
        redirectTo: 'redireccionador/cexplotacion/:data',
      },
      {
        path: 'map/:data',
        redirectTo: 'redireccionador/map/:data',
      },
      {
        path: 'gestion-tareas/:data',
        redirectTo: 'redireccionador/gestion-tareas/:data',
      },
      {
        path: 'gestion-recursos/:data',
        redirectTo: 'redireccionador/gestion-recursos/:data',
      },
      {
        path: 'energia-informe-mensual/:data',
        redirectTo: 'redireccionador/energia-informe-mensual/:data',
      },
      {
        path: 'energia-informe-semanal/:data',
        redirectTo: 'redireccionador/energia-informe-semanal/:data',
      },
      {
        path: 'energia-informe-cierre-mensual/:data',
        redirectTo: '/redireccionador/energia-informe-cierre-mensual/:data',
      },
      {
        path: 'ajuste-bombas/:data',
        redirectTo: 'redireccionador/ajuste-bombas/:data',
      },
      {
        path: 'gestion-dispositivos/:data',
        redirectTo: 'redireccionador/gestion-dispositivos/:data',
      },
      {
        path: 'indices/:data',
        redirectTo: 'redireccionador/indices/:data',
      },
      // Esta ruta es publica y permite el acceso directo a las diferentes rutas (Distribuidor)
      {
        path: 'sanidad/:data',
        redirectTo: 'redireccionador/sanidad/:data',
      },
      // Esta ruta es publica y permite el acceso directo a las diferentes rutas (Distribuidor)

      {
        path: 'sensores',
        loadChildren: () =>
          import('./pages/sensores/sensores.module').then(
            m => m.SensoresPageModule
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['sensores'])],
      },

      {
        path: 'sanidad-vegetal-v2',
        pathMatch: 'full',
        redirectTo: 'sanidad-vegetal',
      },
      {
        path: 'sanidad-vegetal-v2/:section',
        redirectTo: 'sanidad-vegetal/:section',
      },

      {
        path: 'sanidad-vegetal',
        loadChildren: () =>
          import('./pages/sanidad-vegetal/sanidad-vegetal.module').then(
            m => m.SanidadVegetalPageModule
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        // canMatch: [rolesGuard(['sanidad'])], // TODO: Descomentar en producción
      },

      {
        path: '4sig',
        loadComponent: () =>
          import('./pages/visorgis/components/gis/gis.component').then(
            c => c.GisComponent
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canLoad: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['4sig'])],
      },
      {
        path: 'teledeteccion',
        loadComponent: () =>
          import('./pages/teledeteccion/teledeteccion.component').then(
            c => c.TeledeteccionComponent
          ),
        canActivate: [ValidarTokenGuard, ValidarModoGuard],
        canMatch: [rolesGuard(['teledeteccion'])],
      },
    ],
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login.page').then(c => c.LoginPage),
    loadChildren: () =>
      import('./pages/login/login.page').then(c => c.LoginPage.routes),
  },

  {
    path: '**',
    loadChildren: () =>
      import('./pages/home/home.module').then(m => m.HomePageModule),
    canActivate: [ValidarTokenGuard, ValidarModoGuard],
    canLoad: [ValidarTokenGuard, ValidarModoGuard],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: NoPreloading,
      urlUpdateStrategy: 'eager',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
